import React, { useContext, ReactNode, useState, useCallback, SetStateAction, useEffect } from 'react';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { PanelViews } from '../../shared/types/enums/PanelViews';
import { ISidePanelContext } from '../context/ISidePanelContext';
import SidePanelContext from '../context/SidePanelContext';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';

export const useSidePanel = (): ISidePanelContext => useContext<ISidePanelContext>(SidePanelContext);

interface ISidePanelProviderProps {
  children: ReactNode;
}

const SidePanelProvider = ({ children }: ISidePanelProviderProps): JSX.Element => {
  const [isOpen, _setIsOpen] = usePersistedState<boolean>(PersistedStateKeys.SidePanelOpen, false);
  const [currentView, setCurrentView] = usePersistedState<PanelViews>(PersistedStateKeys.PanelView, PanelViews.None);
  const [isInvoicingPanelDirty, setIsInvoicingPanelDirty] = useState(false);
  const [isZIndexIncreased, setIsZIndexIncreased] = useState<boolean>(false);

  const { hasModule, loaded: modulesLoaded } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);


  // Ensure that when the AppointmentsStandard module is unavailable (for example, after switching from a standard to a non-standard clinic),
  // the side panel reverts to None instead of remaining in the Search view from a previous session
  useEffect(() => {

    if (!modulesLoaded || isStandardClinic) {
      return;
    }

    const allowedNonStandardViews = [
      PanelViews.Scheduler,
      PanelViews.Menu,
    ];

    if (currentView !== PanelViews.None && !allowedNonStandardViews.includes(currentView)) {
      setCurrentView(PanelViews.Scheduler);
    }
  }, [currentView, isStandardClinic, modulesLoaded, setCurrentView]);

  const setIsOpen = useCallback(
    (value: SetStateAction<boolean>) => {
      if (!value) {
        setIsZIndexIncreased(false);
      }
      _setIsOpen(value);
    },
    [_setIsOpen],
  );

  return (
    <SidePanelContext.Provider
      value={{
        isOpen,
        setIsOpen,
        currentView,
        setCurrentView,
        isInvoicingPanelDirty,
        setIsInvoicingPanelDirty,
        setIsZIndexIncreased,
        isZIndexIncreased,
      }}
    >
      {children}
    </SidePanelContext.Provider>
  );
};

export default SidePanelProvider;
